import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "@/lib/apiClient";
import {
  AlertTriangle,
  ArrowLeft,
  Printer,
  Pill,
  Sparkles,
  FlaskConical,
  ListChecks,
  RefreshCw,
  ClipboardCheck,
  Download,
  Zap,
  ChevronDown,
  ChevronRight,
  Info,
  FileText,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import DisclaimerV1 from "@/components/DisclaimerV1";
import FicheSyntheseMedicale from "./FicheSyntheseMedicale";

/* ─── labels ──────────────────────────────────────────────────────────────── */

const DOC_TYPE_LABELS = {
  ordonnance:        "Ordonnance",
  crh:               "Compte-rendu d'hospitalisation",
  biologie:          "Bilan biologique",
  lettre_liaison:    "Lettre de liaison",
  anamnese:          "Anamnèse patient",
  autre:             "Document",
  previous_analysis: "Analyse précédente",
};

const DOC_TYPE_COLORS = {
  ordonnance:     "bg-blue-50 text-blue-700 border-blue-200",
  crh:            "bg-purple-50 text-purple-700 border-purple-200",
  biologie:       "bg-teal-50 text-teal-700 border-teal-200",
  lettre_liaison: "bg-indigo-50 text-indigo-700 border-indigo-200",
  anamnese:       "bg-green-50 text-green-700 border-green-200",
  autre:          "bg-slate-50 text-slate-600 border-slate-200",
};

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const SEVERITY_COLORS = {
  high:   "border-l-red-500 bg-red-50/20",
  medium: "border-l-amber-400 bg-amber-50/20",
  low:    "border-l-yellow-400 bg-yellow-50/20",
  "RISQUE CRITIQUE":          "border-l-red-600 bg-red-50/30",
  "ASSOCIATION_DECONSEILLÉE": "border-l-amber-500 bg-amber-50/30",
  "PRÉCAUTION_D'EMPLOI":      "border-l-yellow-400 bg-yellow-50/20",
  "PRÉCAUTION":               "border-l-yellow-400 bg-yellow-50/20",
};

const SEVERITY_BADGE = {
  high:   "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low:    "bg-yellow-100 text-yellow-700 border-yellow-200",
};

const SEVERITY_LABELS_FR = {
  high:   "Priorité haute",
  medium: "Priorité modérée",
  low:    "Priorité basse",
};

const INTERACTION_SEVERITY_LABELS = {
  "RISQUE CRITIQUE":          "Interaction critique BDPM",
  "ASSOCIATION_DECONSEILLÉE": "Association déconseillée BDPM",
  "PRÉCAUTION_D'EMPLOI":      "Précaution d'emploi BDPM",
  "PRÉCAUTION":               "Précaution d'emploi BDPM",
};

const interactionRank = (s) => {
  const u = s?.toString().toUpperCase();
  if (u === "RISQUE CRITIQUE") return 0;
  if (u === "ASSOCIATION_DECONSEILLÉE") return 1;
  return 2;
};

/* ─── collapsible section ─────────────────────────────────────────────────── */

function Section({ title, icon: Icon, iconColor = "text-blue-600", count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-slate-50 transition-colors text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
          <span className="font-semibold text-slate-800">{title}</span>
          {count !== undefined && (
            <Badge variant="outline" className="text-xs font-normal">
              {count}
            </Badge>
          )}
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && <div className="px-5 py-4 bg-white border-t space-y-3">{children}</div>}
    </div>
  );
}

/* ─── empty state ─────────────────────────────────────────────────────────── */

function Empty({ label }) {
  return (
    <p className="text-sm text-slate-400 italic py-2">{label || "Aucun élément"}</p>
  );
}

/* ─── source card (tracabilité) ───────────────────────────────────────────── */

function SourceCard({ source, index }) {
  const [textOpen, setTextOpen] = useState(false);
  const label = DOC_TYPE_LABELS[source.doc_type] || "Document";
  const colorClass = DOC_TYPE_COLORS[source.doc_type] || DOC_TYPE_COLORS.autre;
  const hasText = source.text && source.text.trim().length > 0;

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Source header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50">
        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <div className="flex-1 flex items-center gap-2 flex-wrap min-w-0">
          <Badge variant="outline" className={`text-xs font-medium ${colorClass}`}>
            {label}
          </Badge>
          {source.doc_date && (
            <span className="text-xs text-slate-500">
              {new Date(source.doc_date).toLocaleDateString("fr-FR", {
                day: "numeric", month: "long", year: "numeric",
              })}
            </span>
          )}
          {source.reliability === "declarative" && (
            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-200">
              Déclaratif
            </Badge>
          )}
        </div>
        {hasText ? (
          <button
            onClick={() => setTextOpen(!textOpen)}
            className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium flex-shrink-0"
          >
            <Eye className="w-3.5 h-3.5" />
            {textOpen ? "Masquer" : "Voir le texte extrait"}
          </button>
        ) : (
          <span className="text-xs text-slate-400 italic flex-shrink-0">Aucun texte extrait</span>
        )}
      </div>

      {/* Extracted text — expandable */}
      {textOpen && hasText && (
        <div className="border-t px-4 py-3">
          <p className="text-xs text-slate-400 mb-2 font-medium uppercase tracking-wide">
            Texte extrait — source {index + 1}
          </p>
          <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto bg-slate-50 rounded p-3 border">
            {source.text.trim()}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ─── main component ──────────────────────────────────────────────────────── */

export default function AnalysisResult() {
  const { id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isPractitionerValidated, setIsPractitionerValidated] = useState(false);
  const [savingPrescription, setSavingPrescription] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => { fetchAnalysis(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalysis = async () => {
    try {
      const res = await apiClient.get(`/analyses/${id}`);
      setAnalysis(res.data);
      if (res.data.patient_id) {
        try {
          const pr = await apiClient.get(`/patients/${res.data.patient_id}`);
          setPatient(pr.data);
        } catch (_) {}
      }
    } catch {
      setError("Analyse non trouvée");
    } finally {
      setLoading(false);
    }
  };

  const handleValidateAndExport = async () => {
    if (!isPractitionerValidated) return;
    setSavingPrescription(true);
    try {
      await apiClient.post(`/analyses/${id}/validate-prescription`, {
        validated_at: new Date().toISOString(),
        practitioner_validation: true,
      });
      handleExportPDF();
    } catch {
      alert("Erreur lors de la validation. Veuillez réessayer.");
    } finally {
      setSavingPrescription(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPdf(true);
    try {
      const response = await apiClient.get(`/analyses/${id}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", `GeriaScan_Analyse_${id.slice(0, 8)}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("Erreur lors de la génération du PDF. Veuillez réessayer.");
    } finally {
      setExportingPdf(false);
    }
  };

  /* ── loading / error states ── */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Chargement de l&rsquo;analyse…</p>
        </div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-slate-900 mb-2">{error || "Analyse introuvable"}</h2>
        <Link to="/history">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l&rsquo;historique
          </Button>
        </Link>
      </div>
    );
  }

  /* ── resolved fields ── */

  const aiExpert      = analysis.ai_expert_analysis || {};
  const confidence    = aiExpert.confidence_score ?? 0;
  const isLowConf     = confidence < 0.85;
  const stopps        = analysis.stopp_notifications || [];
  const starts        = analysis.start_recommendations || [];
  const beers         = analysis.beers_notifications || [];
  const laroche       = analysis.laroche_notifications || [];
  const drugInter     = analysis.drug_interactions || [];
  const classDupes    = analysis.class_duplicates || [];
  const medsAtc       = analysis.medications_with_atc || [];
  const concordance   = analysis.concordance_issues || [];
  const hasRecos      = analysis.has_recommendations || [];

  /* ─────────────────────────────────────────────────────────────────────── */

  return (
    <div className="max-w-4xl mx-auto pb-16 space-y-6" data-testid="analysis-result-page">

      {/* ── Navigation header ── */}
      <div className="flex items-start justify-between pt-4 no-print">
        <div>
          <Link to="/history" className="text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-2 text-sm">
            <ArrowLeft className="h-4 w-4" />
            Retour à l&rsquo;historique
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Fiche documentaire</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {patient && <span className="font-medium text-slate-700 mr-2">{patient.name || patient.id}</span>}
            Générée le{" "}
            {new Date(analysis.created_at).toLocaleDateString("fr-FR", {
              day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.print()}
          >
            <Printer className="mr-1.5 h-4 w-4" />
            Imprimer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportPDF}
            disabled={exportingPdf || !isPractitionerValidated}
            data-testid="export-pdf-btn"
          >
            {exportingPdf ? (
              <><RefreshCw className="mr-1.5 h-4 w-4 animate-spin" />Export…</>
            ) : (
              <><Download className="mr-1.5 h-4 w-4" />Export PDF</>
            )}
          </Button>
        </div>
      </div>

      {/* ── Confidence warning ── */}
      {isLowConf && (
        <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">
              Score de confiance réduit ({(confidence * 100).toFixed(0)}%)
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Une vérification manuelle du document source est recommandée avant toute utilisation.
            </p>
          </div>
        </div>
      )}

      {/* ── Sources analysées ── */}
      {(analysis.sources || []).length > 0 && (
        <Section
          title="Sources analysées"
          icon={FileText}
          iconColor="text-slate-500"
          count={(analysis.sources || []).length}
          defaultOpen={true}
        >
          <p className="text-xs text-slate-500 mb-3">
            Vérifiez que les documents ci-dessous correspondent bien aux sources transmises avant de consulter l&rsquo;analyse.
          </p>
          <div className="space-y-2">
            {(analysis.sources || []).map((src, i) => (
              <SourceCard key={i} source={src} index={i} />
            ))}
          </div>
        </Section>
      )}

      {/* ── Résumé ── */}
      {(analysis.summary || (analysis.final_recommendations || []).length > 0) && (
        <Section title="Résumé" icon={Info} iconColor="text-blue-500">
          {analysis.summary && (
            <p className="text-sm text-slate-700 leading-relaxed">{analysis.summary}</p>
          )}
          {(analysis.final_recommendations || []).length > 0 && (
            <ul className="mt-2 space-y-1">
              {analysis.final_recommendations.map((r, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-slate-400">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
          {analysis.patient_renal_stage && (
            <p className="text-xs text-slate-500 mt-2">
              Stade rénal : <strong>{analysis.patient_renal_stage}</strong>
              {analysis.patient_dfg && ` — DFG ${analysis.patient_dfg} mL/min`}
            </p>
          )}
        </Section>
      )}

      {/* ── Médicaments analysés ── */}
      <Section
        title="Médicaments analysés"
        icon={Pill}
        iconColor="text-emerald-600"
        count={medsAtc.length}
      >
        {medsAtc.length === 0 ? (
          <Empty label="Aucun médicament extrait" />
        ) : (
          <div className="overflow-x-auto -mx-5 px-5">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Dosage</TableHead>
                  <TableHead>Posologie</TableHead>
                  <TableHead>Code ATC</TableHead>
                  <TableHead>Adaptation rénale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {medsAtc.map((med, i) => (
                  <TableRow key={i} className={med.requires_adjustment ? "bg-amber-50/40" : ""}>
                    <TableCell className="font-medium">{med.name}</TableCell>
                    <TableCell>{med.dosage || "—"}</TableCell>
                    <TableCell>{med.frequency || "—"}</TableCell>
                    <TableCell>
                      {med.atc_code ? (
                        <span className="font-mono text-xs">{med.atc_code}</span>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {med.requires_adjustment ? (
                        <span className="text-xs text-amber-700 font-medium">
                          {med.dose_adjustment_comment || med.recommended_dose || "Adaptation requise"}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Aucune</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      {/* ── Concordance antécédents / traitements ── */}
      <Section
        title="Concordance antécédents / traitements"
        icon={ListChecks}
        iconColor="text-slate-500"
        count={concordance.length}
      >
        {analysis.concordance_summary && (
          <p className="text-xs text-slate-500 mb-3">{analysis.concordance_summary}</p>
        )}
        {concordance.length === 0 ? (
          <Empty label="Aucun problème de concordance détecté" />
        ) : (
          concordance.map((issue, i) => (
            <div
              key={i}
              className={`border-l-4 pl-3 py-2 rounded-r ${SEVERITY_COLORS[issue.severity] || "border-l-slate-300"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className={`text-xs ${SEVERITY_BADGE[issue.severity] || ""}`}>
                  {SEVERITY_LABELS_FR[issue.severity] || issue.severity}
                </Badge>
                <span className="text-xs font-medium text-slate-600">{issue.antecedent}</span>
                <span className="text-xs text-slate-400">→</span>
                <span className="text-xs text-slate-600">{issue.expected_treatment}</span>
              </div>
              {issue.recommendation && (
                <p className="text-xs text-slate-600 italic">{issue.recommendation}</p>
              )}
            </div>
          ))
        )}
      </Section>

      {/* ── Référentiel STOPP ── */}
      <Section
        title="Référentiel STOPP"
        icon={AlertTriangle}
        iconColor="text-red-500"
        count={stopps.length}
      >
        {stopps.length === 0 ? (
          <Empty label="Aucun critère STOPP identifié" />
        ) : (
          stopps.map((n, i) => (
            <div
              key={i}
              className={`border-l-4 pl-3 py-2 rounded-r ${SEVERITY_COLORS[n.severity] || "border-l-red-400"}`}
            >
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {n.code && (
                  <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                    {n.code}
                  </span>
                )}
                {n.category && (
                  <span className="text-xs text-slate-500">{n.category}</span>
                )}
                {n.severity && (
                  <Badge variant="outline" className={`text-xs ${SEVERITY_BADGE[n.severity] || ""}`}>
                    {SEVERITY_LABELS_FR[n.severity] || n.severity}
                  </Badge>
                )}
              </div>
              {n.medication && (
                <p className="text-sm font-medium text-slate-800">{n.medication}</p>
              )}
              {n.reason && (
                <p className="text-xs text-slate-600 mt-0.5">{n.reason}</p>
              )}
              {n.recommendation && (
                <p className="text-xs text-blue-700 mt-1 italic">
                  Le référentiel STOPP suggère : {n.recommendation}
                </p>
              )}
              {isLowConf && (
                <p className="text-xs text-amber-600 mt-1">
                  ⚠ Score de confiance réduit ({(confidence * 100).toFixed(0)}%) — vérifier sur le document source
                </p>
              )}
            </div>
          ))
        )}
      </Section>

      {/* ── Référentiel START ── */}
      <Section
        title="Référentiel START"
        icon={ListChecks}
        iconColor="text-emerald-500"
        count={starts.length}
      >
        {starts.length === 0 ? (
          <Empty label="Aucun critère START identifié" />
        ) : (
          starts.map((r, i) => (
            <div key={i} className="border-l-4 border-l-emerald-500 pl-3 py-2 rounded-r bg-emerald-50/20">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {r.code && (
                  <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                    {r.code}
                  </span>
                )}
                {r.category && (
                  <span className="text-xs text-slate-500">{r.category}</span>
                )}
              </div>
              {r.condition && (
                <p className="text-xs text-slate-600">{r.condition}</p>
              )}
              {r.recommended_medication && (
                <p className="text-sm font-medium text-emerald-800">{r.recommended_medication}</p>
              )}
              {r.reason && (
                <p className="text-xs text-slate-600 mt-0.5 italic">{r.reason}</p>
              )}
            </div>
          ))
        )}
      </Section>

      {/* ── Critères de Beers 2023 ── */}
      {beers.length > 0 && (
        <Section
          title="Critères de Beers 2023"
          icon={FlaskConical}
          iconColor="text-purple-500"
          count={beers.length}
        >
          {beers.map((b, i) => (
            <div key={i} className="border-l-4 border-l-purple-400 pl-3 py-2 rounded-r bg-purple-50/20">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {b.type && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                    {b.type}
                  </Badge>
                )}
                {b.medication && (
                  <span className="text-sm font-medium text-slate-800">{b.medication}</span>
                )}
              </div>
              {b.reason && (
                <p className="text-xs text-slate-600">{b.reason}</p>
              )}
              {b.recommendation && (
                <p className="text-xs text-purple-700 mt-1 italic">{b.recommendation}</p>
              )}
              {b.source && (
                <p className="text-xs text-slate-400 mt-1">{b.source}</p>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* ── Liste de Laroche ── */}
      <Section
        title="Liste de Laroche"
        icon={FlaskConical}
        iconColor="text-amber-500"
        count={laroche.length}
        defaultOpen={laroche.length > 0}
      >
        {laroche.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            Aucun médicament potentiellement inapproprié détecté selon la liste de Laroche.
          </p>
        ) : (
          laroche.map((n, i) => (
            <div key={i} className="border-l-4 border-l-amber-400 pl-3 py-2 rounded-r bg-amber-50/20">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {n.classe && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                    {n.classe}
                  </Badge>
                )}
                {n.medication && (
                  <span className="text-sm font-medium text-slate-800">{n.medication}</span>
                )}
              </div>
              {n.reason && (
                <p className="text-xs text-slate-600">{n.reason}</p>
              )}
              {n.alternatives_documentees && n.alternatives_documentees.length > 0 && (
                <p className="text-xs text-amber-700 mt-1 italic">
                  Alternatives documentées : {n.alternatives_documentees.join(", ")}
                </p>
              )}
              {n.referentiel_source && (
                <p className="text-xs text-slate-400 mt-1">{n.referentiel_source}</p>
              )}
            </div>
          ))
        )}
      </Section>

      {/* ── Recommandations HAS ── */}
      {hasRecos.length > 0 && (
        <Section
          title="Recommandations HAS"
          icon={ListChecks}
          iconColor="text-blue-600"
          count={hasRecos.length}
        >
          {hasRecos.map((r, i) => (
            <div key={i} className="border-l-4 border-l-blue-400 pl-3 py-2 rounded-r bg-blue-50/20">
              {r.category && (
                <p className="text-xs font-semibold text-blue-700 mb-0.5">{r.category}</p>
              )}
              <p className="text-sm text-slate-700">{r.recommendation}</p>
              {(r.source || r.year) && (
                <p className="text-xs text-slate-400 mt-1">
                  {r.source}{r.year ? ` (${r.year})` : ""}
                </p>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* ── Interactions médicamenteuses ── */}
      <Section
        title="Interactions médicamenteuses"
        icon={Zap}
        iconColor="text-orange-500"
        count={drugInter.length + classDupes.length}
        defaultOpen={drugInter.length > 0 || classDupes.length > 0}
      >
        {drugInter.length === 0 && classDupes.length === 0 ? (
          <Empty label="Aucune interaction détectée" />
        ) : (
          <>
            {[...drugInter]
              .sort((a, b) => interactionRank(a.severity) - interactionRank(b.severity))
              .map((inter, i) => (
                <div
                  key={i}
                  className={`border-l-4 pl-3 py-2 rounded-r ${SEVERITY_COLORS[inter.severity] || "border-l-orange-400"}`}
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className={`text-xs ${
                      interactionRank(inter.severity) === 0
                        ? "bg-red-100 text-red-700 border-red-200"
                        : interactionRank(inter.severity) === 1
                        ? "bg-amber-100 text-amber-700 border-amber-200"
                        : "bg-yellow-100 text-yellow-700 border-yellow-200"
                    }`}>
                      {INTERACTION_SEVERITY_LABELS[inter.severity] || inter.severity}
                    </Badge>
                  </div>
                  <p className="text-sm font-semibold text-slate-800">
                    {inter.medication_1}
                    <Zap className="inline w-3.5 h-3.5 mx-1 text-amber-500" />
                    {inter.medication_2}
                  </p>
                  {inter.description && (
                    <p className="text-xs text-slate-600 mt-0.5">{inter.description}</p>
                  )}
                  {inter.recommendation && (
                    <p className="text-xs text-slate-500 mt-1 italic">→ {inter.recommendation}</p>
                  )}
                </div>
              ))}

            {classDupes.map((dup, i) => (
              <div key={`dup-${i}`} className="border-l-4 border-l-orange-300 pl-3 py-2 rounded-r bg-orange-50/20">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                    Duplication de classe
                  </Badge>
                  {dup.therapeutic_class && (
                    <span className="text-xs text-slate-600">{dup.therapeutic_class}</span>
                  )}
                </div>
                {dup.medications?.length > 0 && (
                  <p className="text-sm font-medium text-slate-800">
                    {dup.medications.join(" · ")}
                  </p>
                )}
                {dup.message && (
                  <p className="text-xs text-slate-600 mt-0.5">{dup.message}</p>
                )}
                {dup.recommendation && (
                  <p className="text-xs text-slate-500 mt-1 italic">→ {dup.recommendation}</p>
                )}
              </div>
            ))}
          </>
        )}
      </Section>

      {/* ── Synthèse IA ── */}
      <Section
        title="Synthèse experte IA"
        icon={Sparkles}
        iconColor="text-amber-500"
      >
        {/* Confidence + model info */}
        <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
          <span>
            Score de confiance :
            <span className={`ml-1 font-semibold ${isLowConf ? "text-amber-600" : "text-emerald-600"}`}>
              {(confidence * 100).toFixed(0)}%
            </span>
          </span>
          {aiExpert.ai_models_used?.map((m, i) => (
            <Badge key={i} variant="outline" className="text-[10px] text-slate-400">
              {m}
            </Badge>
          ))}
          {aiExpert.consensus_validated && (
            <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">
              Validé
            </Badge>
          )}
        </div>

        {isLowConf && (
          <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
            Analyse à score de confiance réduit. Vérification manuelle recommandée.
          </div>
        )}

        {aiExpert.global_synthesis && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Synthèse globale
            </p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {aiExpert.global_synthesis}
            </p>
          </div>
        )}

        {aiExpert.pharmacology_review && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Revue pharmacologique
            </p>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {aiExpert.pharmacology_review}
            </p>
          </div>
        )}

        {aiExpert.geriatric_recommendations?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Recommandations gériatriques
            </p>
            <ul className="space-y-1">
              {aiExpert.geriatric_recommendations.map((r, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-slate-400">•</span><span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {aiExpert.dose_adjustments?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Adaptations posologiques suggérées
            </p>
            <ul className="space-y-1">
              {aiExpert.dose_adjustments.map((r, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-slate-400">•</span><span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {aiExpert.pharmacovigilance_notifications?.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Pharmacovigilance
            </p>
            <ul className="space-y-1">
              {aiExpert.pharmacovigilance_notifications.map((r, i) => (
                <li key={i} className="text-sm text-slate-700 flex gap-2">
                  <span className="text-slate-400">•</span><span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(!aiExpert.global_synthesis && !aiExpert.pharmacology_review) && (
          <Empty label="Synthèse IA non disponible" />
        )}
      </Section>

      {/* ── Fiche de Synthèse Médicale ── */}
      <FicheSyntheseMedicale analysis={analysis} patient={patient} />

      {/* ── Validation praticien ── */}
      <Card className="border-2 border-emerald-400 bg-emerald-50/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-emerald-800">
            <ClipboardCheck className="h-5 w-5" />
            Validation du praticien
          </CardTitle>
          <CardDescription className="text-emerald-700">
            Requis avant tout export. GeriaScan est un outil documentaire — toute décision
            thérapeutique relève de la seule responsabilité du praticien qualifié.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-emerald-200">
            <Checkbox
              id="practitioner-validation"
              checked={isPractitionerValidated}
              onCheckedChange={setIsPractitionerValidated}
            />
            <label
              htmlFor="practitioner-validation"
              className="text-sm font-medium text-emerald-900 cursor-pointer leading-tight"
            >
              Je valide cette synthèse documentaire en tant que praticien qualifié
            </label>
          </div>
          <Button
            size="lg"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 disabled:opacity-50"
            disabled={!isPractitionerValidated || savingPrescription}
            onClick={handleValidateAndExport}
            data-testid="validate-and-export-btn"
          >
            {savingPrescription ? (
              <><RefreshCw className="mr-2 h-5 w-5 animate-spin" />Validation…</>
            ) : (
              <><Download className="mr-2 h-5 w-5" />Valider et exporter PDF</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* ── MDR disclaimer ── */}
      <DisclaimerV1 />

      {/* ── [TEST] Raw JSON — à retirer ── */}
      <details className="mt-6 border rounded-lg overflow-hidden text-xs">
        <summary className="cursor-pointer px-4 py-3 bg-slate-100 hover:bg-slate-200 font-mono text-slate-500 select-none">
          [TEST — À RETIRER] Données brutes JSON
        </summary>
        <pre className="p-4 bg-slate-950 text-green-400 overflow-x-auto max-h-[60vh] whitespace-pre-wrap break-words">
          {JSON.stringify(analysis, null, 2)}
        </pre>
      </details>
    </div>
  );
}
